import { doc, runTransaction, updateDoc } from "firebase/firestore";
import {
    ref,
    uploadBytesResumable,
    getDownloadURL,
    deleteObject
} from "firebase/storage";
import { updateProfile } from "firebase/auth";
import { auth, fs, storage } from "../firebase.ts";
import { pickFile } from "../components/global.ts";
import { Media } from "./Media.ts";

export class Humanity {
    private media = new Media();

    async updateDisplayName(displayName: string): Promise<string> {
        const user = auth.currentUser;
        if (!user) throw new Error("Authenticated users only");

        await updateProfile(user, {
            displayName
        });
        //await user.getIdToken(true); // not reliable
        const userDocRef = doc(fs, "users", user.uid);
        await updateDoc(userDocRef, { displayName });

        return "Display name updated successfully!";
    }

    async unremoveBackground(): Promise<string> {
        const user = auth.currentUser;
        if (!user) throw new Error("Authenticated users only");

        const fileRef = ref(storage, `users/${user.uid}/avatars/avatar`);
        const photoURL = await getDownloadURL(fileRef);

        await updateProfile(user, { photoURL });
        //await user.getIdToken(true); // not reliable
        const userDocRef = doc(fs, "users", user.uid);
        await updateDoc(userDocRef, { photoURL });

        return photoURL;
    }

    async updateAvatar(
        progressCallback?: (progress: number) => void,
        useCamera: boolean = false
    ): Promise<string> {
        let file = await pickFile({
            capture: useCamera ? "camera" : undefined,
            accept: "image/*" // ".jpg,.png,.webp,.jpeg,.svg" // iphone HEIC files don’t work; no gif bc animation
        });
        if (file) {
            file = await this.media.createAvatarFromFile(file);
            return this.saveAvatar(file, progressCallback);
        }
        return "";
    }

    private saveAvatar(
        file: File,
        progressCallback?: (progress: number) => void
    ): Promise<string> {
        return new Promise((resolve, reject) => {
            // 1. Create the upload task
            const user = auth.currentUser;
            if (!user) return reject(new Error("Authenticated users only"));

            const storageRef = ref(
                storage,
                `users/${user.uid}/avatars/${file.name === "b-avatar.png" ? "b-avatar" : "avatar"}`
            ); // to replace old one
            const uploadTask = uploadBytesResumable(storageRef, file, {
                customMetadata: {} //TODO: store props in metadata?
            });

            // 2. Register the event listener
            uploadTask.on(
                "state_changed",
                // Observer for progress
                (snapshot) => {
                    const progress = Math.round(
                        (snapshot.bytesTransferred / snapshot.totalBytes) * 100
                    );
                    //console.log("Upload is " + progress + "% done");

                    // Call the progress callback if it exists
                    if (progressCallback) {
                        progressCallback(progress);
                    }
                },
                // Observer for errors
                (error) => {
                    // Handle unsuccessful uploads and reject the promise
                    //console.error("Upload failed:", error);
                    reject(error);
                },
                // Observer for completion
                async () => {
                    try {
                        // Handle successful uploads on complete
                        //console.log("Upload complete, getting download URL...");
                        const photoURL = await getDownloadURL(
                            uploadTask.snapshot.ref
                        );
                        await updateProfile(user, { photoURL });
                        //await user.getIdToken(true); // not reliable
                        const userDocRef = doc(fs, "users", user.uid);
                        await updateDoc(userDocRef, { photoURL });
                        // Resolve the promise with the download URL
                        resolve(photoURL);
                    } catch (error) {
                        // If getting the URL fails, reject the promise
                        //console.error("Failed to get download URL:", error);
                        reject(error);
                    }
                }
            );
        });
    }

    async removeAvatar(): Promise<string> {
        const user = auth.currentUser;
        if (!user) throw new Error("Authenticated users only");
        if (!user.photoURL)
            throw new Error("You do not have a profile photo to delete");

        // 2. Create a reference to the file to delete.
        // Firebase Storage SDK can parse the gs:// or https:// URL to get a reference.
        const imageRef = ref(storage, user.photoURL);
        // 3. Delete the file from Cloud Storage
        await deleteObject(imageRef);
        //console.log("File deleted from Storage successfully.");

        // 4. Update the user's profile in Firebase Auth (set photoURL to null)
        await updateProfile(user, {
            photoURL: "" // cannot be null
        });
        //await user.getIdToken(true); // not reliable
        const userDocRef = doc(fs, "users", user.uid);
        await updateDoc(userDocRef, { photoURL: "" });
        //console.log("User photoURL removed from Auth profile.");

        // 5. Update the UI
        return "Profile photo deleted successfully.";
    }

    // TODO: add client library to remove bad words and reserved words: see ai chat
    async updateUsername(chosenUsername: string): Promise<string> {
        const user = auth.currentUser;
        if (!user) throw new Error("Authenticated users only");

        // 1. Normalize the username (e.g., to lowercase)
        const username = chosenUsername.toLowerCase().trim();

        // 2. Validate format on the client (quick feedback for user)
        const regex = /^[a-z0-9]{3,15}$/;
        if (!regex.test(username)) {
            throw new Error(
                "Invalid username format. Must be 3-15 lowercase letters or numbers."
            );
        }

        // 3. Create references to the two documents we need to touch
        const userDocRef = doc(fs, "users", user.uid);
        const usernameDocRef = doc(fs, "usernames", username);

        // 4. Run the transaction
        await runTransaction(fs, async (transaction) => {
            const usernameDocSnap = await transaction.get(usernameDocRef);
            // Check if username is already taken
            if (usernameDocSnap.exists()) {
                // IMPORTANT: Throwing an error inside a transaction will cause it to
                // automatically abort and rollback.
                throw new Error("Sorry, this username is already taken.");
            }

            // If we get here, the username is available.
            // Let's reserve it and update the user's profile.
            const userDocSnap = await transaction.get(userDocRef); // reads before writes!
            transaction.set(usernameDocRef, { uid: user.uid }); // add, not update
            transaction.update(userDocRef, { username: username });

            // If the user already has a username, we need to delete the old one
            const oldUsername = userDocSnap.data()?.username;
            if (oldUsername) {
                const oldUsernameDocRef = doc(fs, "usernames", oldUsername);
                transaction.delete(oldUsernameDocRef);
            }
        });

        return (
            username +
            " has been claimed successfully! Please note that we reserve the right to remove usernames that are inappropriate or trademarked."
        );
    }

    // deprecated: hangs if user cancels selection
    private async pickFile2(): Promise<File> {
        return new Promise((resolve, reject) => {
            const input = document.createElement("input");
            input.type = "file";
            input.accept = ".jpg,.png";
            input.onchange = () => {
                if (input.files && input.files[0]) resolve(input.files[0]);
                else reject(new Error("No file selected"));
            };
            input.click();
        });
    }
}
